import React, { useEffect, useState } from 'react';
import { Col, Row, getItemWithExpiration } from '../../lib/utils';
import { colors } from '../../lib/colors';
import { Body2Regular, CaptionLight, Subtitle1Bold } from '../../lib/font';
import Modal from '../modal/Modal';
import ColorSquareButton from '../Button/ColorSquareButton';
import WhiteSquareButton from '../Button/WhiteSquareButton';
import { usePublicMemberList } from '@/features/publicMember/hooks';
import { getMemberAppAccountStatus } from '@/features/publicMember/types';
import { useMergePreview, useExecuteMerge } from '@/features/memberMerge/hooks';
import { MergePreviewResponse } from '@/features/memberMerge/types';
import { formatKoreanPhoneNumber, formatKoreanPhoneNumberInput } from '../../utils/phoneNumber';

type Step = 'search' | 'preview' | 'confirm';

export const getMergeTargetBlockReason = (
    member: any
): 'withdrawn' | 'unlinked' | null => {
    if (getMemberAppAccountStatus(member) === 'withdrawn') {
        return 'withdrawn';
    }
    if (member?.memberType === 'temp') {
        return 'unlinked';
    }
    return null;
};

export const getMergeRequestErrorStatus = (error: any): number | null => {
    const status = error?.status ?? error?.response?.status;
    return Number.isFinite(Number(status)) ? Number(status) : null;
};

export const getMergeRequestErrorMessage = (error: any, fallback: string) =>
    error?.serverMessage ||
    error?.response?.data?.message ||
    error?.message ||
    fallback;

const MemberMergeModal = ({
    onClose,
    tempMemberId,
    tempMemberName,
    tempPhoneNumber,
    sourceType = 'temp',
    coachId,
    coachName,
    onMergeComplete,
}: {
    onClose: () => void;
    tempMemberId: string;
    tempMemberName: string;
    tempPhoneNumber?: string;
    /** 병합 출발지 회원 타입. 'temp'면 coachId 필수, 'public'이면 optional */
    sourceType?: 'temp' | 'public';
    coachId?: string;
    coachName?: string;
    onMergeComplete: (originalMemberId: string) => void;
}) => {
    const isPublicSource = sourceType === 'public';
    const [authObject] = useState(getItemWithExpiration('authObject'));
    const centerId = authObject?.centerId;

    const [step, setStep] = useState<Step>('search');
    const [searchQuery, setSearchQuery] = useState(formatKoreanPhoneNumberInput(tempPhoneNumber));
    const [selectedOriginalId, setSelectedOriginalId] = useState('');
    const [searchError, setSearchError] = useState('');
    const [mergeError, setMergeError] = useState('');

    const { data: searchData } = usePublicMemberList({
        centerId,
        search: searchQuery || undefined,
        memberType: 'all',
        limit: 20,
        enabled: searchQuery.length >= 2,
    });
    const searchResults = (searchData?.results ?? []).filter(
        (m: any) =>
            m.memberId !== tempMemberId &&
            m._id !== tempMemberId &&
            (m.memberType === 'original' || m.memberType === 'temp')
    );

    const {
        data: preview,
        isLoading: previewLoading,
        isError: previewError,
        error: previewRequestError,
    } = useMergePreview({
        tempMemberId,
        originalMemberId: selectedOriginalId,
        enabled: step === 'preview' && !!selectedOriginalId,
    });

    const mergeMutation = useExecuteMerge();

    const resetToSearch = (message = '') => {
        setSelectedOriginalId('');
        setMergeError('');
        setSearchError(message);
        setStep('search');
    };

    // 검색 후 대상/출발지가 탈퇴한 stale preview는 동일 ID로 재시도하지 않는다.
    useEffect(() => {
        if (
            !previewError ||
            getMergeRequestErrorStatus(previewRequestError) !== 409
        ) {
            return;
        }
        resetToSearch(
            getMergeRequestErrorMessage(
                previewRequestError,
                '탈퇴했거나 탈퇴 처리 중인 회원은 병합할 수 없습니다.'
            )
        );
    }, [previewError, previewRequestError]);

    const handleSelectOriginal = (member: any) => {
        if (getMergeTargetBlockReason(member)) {
            return;
        }
        setSearchError('');
        setSelectedOriginalId(member.memberId);
        setStep('preview');
    };

    const handleMerge = async () => {
        // temp 병합은 coachId 필수, public 병합은 optional
        if (!isPublicSource && !coachId) {
            setMergeError('담당 강사 정보가 없습니다. 강사를 먼저 배정해주세요.');
            return;
        }
        setMergeError('');
        try {
            const result = await mergeMutation.mutateAsync({
                tempMemberId,
                originalMemberId: selectedOriginalId,
                ...(coachId ? { coachId } : {}),
            });
            onMergeComplete(result?.data?.mergedMemberId || selectedOriginalId);
        } catch (err: any) {
            const msg = getMergeRequestErrorMessage(err, '병합에 실패했습니다.');
            if (getMergeRequestErrorStatus(err) === 409) {
                resetToSearch(msg);
                return;
            }
            setMergeError(msg);
            setStep('confirm');
        }
    };

    return (
        <Modal isCloseButton setIsVisible={mergeMutation.isLoading ? () => {} : onClose} whiteViewWidth={600}>
            <style>{`
                @keyframes merge-slide-in {
                    from { opacity: 0; transform: translateY(-8px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                .merge-step-enter { animation: merge-slide-in 0.2s ease; }
            `}</style>
            <Col style={{ padding: '32px 36px', overflow: 'auto', maxHeight: '85vh' }}>
                <Subtitle1Bold style={{ marginBottom: 4 }}>회원 병합</Subtitle1Bold>
                <CaptionLight color={colors.LAVEL_1} style={{ marginBottom: 24 }}>
                    {isPublicSource
                        ? '퍼블릭 회원의 데이터를 연동 회원 계정으로 이관합니다'
                        : '미연동 회원의 데이터를 연동 회원 계정으로 이관합니다'}
                </CaptionLight>

                {/* Step indicator */}
                <Row style={{ gap: 0, marginBottom: 24 }}>
                    {(['search', 'preview', 'confirm'] as Step[]).map((s, i) => {
                        const labels = ['대상 검색', '미리보기', '확인'];
                        const active = s === step;
                        const done = (['search', 'preview', 'confirm'] as Step[]).indexOf(step) > i;
                        return (
                            <Row key={s} style={{ flex: 1, alignItems: 'center', gap: 8 }}>
                                <div style={{
                                    width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 12, fontWeight: 700,
                                    backgroundColor: active ? '#2563EB' : done ? '#10B981' : '#E5E7EB',
                                    color: active || done ? '#fff' : '#9CA3AF',
                                }}>
                                    {done ? '✓' : i + 1}
                                </div>
                                <span style={{ fontSize: 13, fontWeight: active ? 700 : 400, color: active ? '#111827' : '#9CA3AF' }}>
                                    {labels[i]}
                                </span>
                                {i < 2 && <div style={{ flex: 1, height: 1, backgroundColor: '#E5E7EB', margin: '0 8px' }} />}
                            </Row>
                        );
                    })}
                </Row>

                {/* 출발지 회원 정보 (temp 또는 public) */}
                <Col style={{ padding: 14, backgroundColor: '#FEF3C7', borderRadius: 10, border: '1px solid #FDE68A', marginBottom: 20 }}>
                    <Row style={{ alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4, backgroundColor: '#F59E0B', color: '#fff' }}>
                            {isPublicSource ? '퍼블릭 회원' : '미연동 회원'}
                        </span>
                        <span style={{ fontSize: 15, fontWeight: 600, color: '#92400E' }}>{tempMemberName}</span>
                        {tempPhoneNumber && <span style={{ fontSize: 13, color: '#92400E' }}>{tempPhoneNumber}</span>}
                        {coachName && <span style={{ fontSize: 13, color: '#92400E' }}>담당: {coachName}</span>}
                    </Row>
                </Col>

                {/* STEP 1: 검색 */}
                {step === 'search' && (
                    <Col className="merge-step-enter" style={{ gap: 12 }}>
                        <Body2Regular color={colors.LAVEL_2}>병합할 연동 회원 검색</Body2Regular>
                        <input
                            placeholder="이름 또는 전화번호 검색"
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchError('');
                                setSearchQuery(e.target.value);
                            }}
                            autoFocus
                            style={{
                                border: '1px solid #D1D5DB', borderRadius: 8, padding: '12px 14px',
                                fontSize: 15, width: '100%', outline: 'none',
                            }}
                        />
                        {searchError && (
                            <div style={{ padding: '10px 14px', borderRadius: 8, backgroundColor: '#FEE2E2', color: '#DC2626', fontSize: 14, fontWeight: 600 }}>
                                {searchError}
                            </div>
                        )}
                        <Col style={{ maxHeight: 240, overflow: 'auto', gap: 4 }}>
                            {searchQuery.length >= 2 && searchResults.length === 0 && (
                                <span style={{ fontSize: 14, color: '#9CA3AF', padding: 12 }}>검색 결과가 없습니다</span>
                            )}
                            {searchResults.map((m: any) => {
                                // 탈퇴 회원은 memberType이 'original'로 남는다. memberType만 보면
                                // 앱을 못 쓰는 계정이 '연동 회원'으로 표시돼 병합 대상이 된다.
                                const blockReason = getMergeTargetBlockReason(m);
                                const isWithdrawn = blockReason === 'withdrawn';
                                const isTemp = blockReason === 'unlinked';
                                const isBlocked = blockReason !== null;
                                return (
                                    <Row
                                        key={m.memberId}
                                        onClick={isBlocked ? undefined : () => handleSelectOriginal(m)}
                                        title={
                                            isWithdrawn
                                                ? '앱을 탈퇴한 회원은 병합할 수 없습니다'
                                                : isTemp
                                                ? '회원이 앱에 연동된 후 병합할 수 있습니다'
                                                : undefined
                                        }
                                        style={{
                                            alignItems: 'center', justifyContent: 'space-between',
                                            padding: '12px 14px',
                                            cursor: isBlocked ? 'not-allowed' : 'pointer',
                                            borderRadius: 10,
                                            border: '1px solid #E5E7EB',
                                            backgroundColor: isBlocked ? '#F9FAFB' : '#fff',
                                            opacity: isBlocked ? 0.7 : 1,
                                        }}
                                    >
                                        <Col>
                                            <Row style={{ alignItems: 'center', gap: 8 }}>
                                                <span style={{
                                                    fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                                                    backgroundColor: isWithdrawn ? '#B42318' : isTemp ? '#9CA3AF' : '#2563EB', color: '#fff',
                                                }}>
                                                    {isWithdrawn ? '앱 탈퇴' : isTemp ? '미연동 회원' : '연동 회원'}
                                                </span>
                                                <span style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>{m.name}</span>
                                            </Row>
                                            <span style={{ fontSize: 13, color: '#6B7280', marginTop: 2 }}>
                                                {formatKoreanPhoneNumber(m.phoneNumber, m.phoneNumber)}{m.coachName ? ` · 담당: ${m.coachName}` : ''}
                                            </span>
                                            {isBlocked && (
                                                <span style={{ fontSize: 12, color: '#9CA3AF', marginTop: 4 }}>
                                                    {isWithdrawn
                                                        ? '앱을 탈퇴한 회원이라 병합할 수 없습니다'
                                                        : '앱 연동 후 병합 가능합니다'}
                                                </span>
                                            )}
                                        </Col>
                                        <span style={{
                                            fontSize: 13, fontWeight: 600,
                                            color: isBlocked ? '#9CA3AF' : '#2563EB',
                                        }}>
                                            {isBlocked ? '선택 불가' : '선택'}
                                        </span>
                                    </Row>
                                );
                            })}
                        </Col>

                        <Row style={{ gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
                            <WhiteSquareButton text="취소" size="small" callBack={onClose} />
                        </Row>
                    </Col>
                )}

                {/* STEP 2: 미리보기 */}
                {step === 'preview' && (
                    <Col className="merge-step-enter" style={{ gap: 16 }}>
                        {previewLoading ? (
                            <Col style={{ alignItems: 'center', padding: '40px 0' }}>
                                <span style={{ fontSize: 14, color: '#9CA3AF' }}>병합 미리보기를 불러오는 중...</span>
                            </Col>
                        ) : previewError ? (
                            <Col style={{ alignItems: 'center', padding: '40px 0', gap: 12 }}>
                                <span style={{ fontSize: 14, color: '#DC2626' }}>
                                    {getMergeRequestErrorMessage(
                                        previewRequestError,
                                        '미리보기를 불러오지 못했습니다'
                                    )}
                                </span>
                                <WhiteSquareButton
                                    text="돌아가기"
                                    size="small"
                                    callBack={() => resetToSearch()}
                                />
                            </Col>
                        ) : preview ? (
                            <>
                                <PreviewComparison preview={preview} />

                                {/* 병합 결과 요약 */}
                                <Col style={{ padding: 16, backgroundColor: '#EFF6FF', borderRadius: 10, border: '1px solid #BFDBFE' }}>
                                    <span style={{ fontSize: 14, fontWeight: 700, color: '#1E40AF', marginBottom: 8 }}>병합 결과</span>
                                    <Row style={{ gap: 16, flexWrap: 'wrap' }}>
                                        <span style={{ fontSize: 14, color: '#1E40AF' }}>PT 수업 {preview.mergeResult.totalSchedules}건</span>
                                        <span style={{ fontSize: 14, color: '#1E40AF' }}>PT 수강권 {preview.mergeResult.totalMemberships}건</span>
                                        {(preview.mergeResult.totalBookings ?? 0) > 0 && (
                                            <span style={{ fontSize: 14, color: '#1E40AF' }}>그룹 예약 {preview.mergeResult.totalBookings}건</span>
                                        )}
                                        {(preview.mergeResult.totalMemberTickets ?? 0) > 0 && (
                                            <span style={{ fontSize: 14, color: '#1E40AF' }}>그룹 수강권 {preview.mergeResult.totalMemberTickets}건</span>
                                        )}
                                        {(preview.mergeResult.totalSubscriptions ?? 0) > 0 && (
                                            <span style={{ fontSize: 14, color: '#1E40AF' }}>시설 이용권 {preview.mergeResult.totalSubscriptions}건</span>
                                        )}
                                    </Row>
                                </Col>

                                <Row style={{ gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
                                    <WhiteSquareButton text="돌아가기" size="small" callBack={() => resetToSearch()} />
                                    <ColorSquareButton text="병합 진행" size="small" callBack={() => setStep('confirm')} />
                                </Row>
                            </>
                        ) : null}
                    </Col>
                )}

                {/* STEP 3: 최종 확인 */}
                {step === 'confirm' && preview && (
                    <Col className="merge-step-enter" style={{ gap: 16 }}>
                        <Col style={{ padding: 20, backgroundColor: '#FEF2F2', borderRadius: 12, border: '1px solid #FECACA', gap: 12 }}>
                            <span style={{ fontSize: 16, fontWeight: 700, color: '#DC2626' }}>병합을 실행하시겠습니까?</span>
                            <Col style={{ gap: 6, fontSize: 14, color: '#7F1D1D' }}>
                                <span>• <b>{preview.temp.name}</b> ({isPublicSource ? '퍼블릭' : '미연동'} 회원) → <b>{preview.original.name}</b> (연동 회원) 으로 데이터 이관</span>
                                <span>• 수업 {preview.temp.scheduleCount}건, 수강권 {preview.temp.membershipCount}건이 이관됩니다</span>
                                <span>• {isPublicSource ? '퍼블릭' : '미연동'} 회원 계정은 비활성화됩니다</span>
                            </Col>
                            <span style={{ fontSize: 13, fontWeight: 700, color: '#DC2626', marginTop: 4 }}>
                                이 작업은 되돌릴 수 없습니다.
                            </span>
                        </Col>

                        {mergeError && (
                            <div style={{ padding: '10px 14px', borderRadius: 8, backgroundColor: '#FEE2E2', color: '#DC2626', fontSize: 14, fontWeight: 600 }}>
                                {mergeError}
                            </div>
                        )}

                        <Row style={{ gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
                            <WhiteSquareButton text="돌아가기" size="small" callBack={() => { setMergeError(''); setStep('preview'); }} isDisabled={mergeMutation.isLoading} />
                            <button
                                onClick={handleMerge}
                                disabled={mergeMutation.isLoading}
                                style={{
                                    padding: '10px 24px', borderRadius: 8, fontSize: 14, fontWeight: 700,
                                    backgroundColor: mergeMutation.isLoading ? '#9CA3AF' : '#DC2626',
                                    color: '#fff', border: 'none',
                                    cursor: mergeMutation.isLoading ? 'not-allowed' : 'pointer',
                                }}
                            >
                                {mergeMutation.isLoading ? '처리 중...' : '병합 실행'}
                            </button>
                        </Row>
                    </Col>
                )}
            </Col>
        </Modal>
    );
};

const PreviewComparison = ({ preview }: { preview: MergePreviewResponse }) => (
    <Row style={{ gap: 12 }}>
        {/* 미연동 회원 */}
        <Col style={{ flex: 1, padding: 16, borderRadius: 10, border: '1px solid #FDE68A', backgroundColor: '#FFFBEB' }}>
            <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4, backgroundColor: '#F59E0B', color: '#fff', alignSelf: 'flex-start', marginBottom: 10 }}>
                이관될 회원
            </span>
            <span style={{ fontSize: 16, fontWeight: 700, color: '#92400E' }}>{preview.temp.name}</span>
            <span style={{ fontSize: 13, color: '#92400E', marginTop: 2 }}>{formatKoreanPhoneNumber(preview.temp.phoneNumber, preview.temp.phoneNumber)}</span>
            <div style={{ height: 1, backgroundColor: '#FDE68A', margin: '10px 0' }} />
            <span style={{ fontSize: 13, color: '#92400E' }}>PT 수업 {preview.temp.scheduleCount}건</span>
            <span style={{ fontSize: 13, color: '#92400E' }}>PT 수강권 {preview.temp.membershipCount}건</span>
            {(preview.temp.bookingCount ?? 0) > 0 && (
                <span style={{ fontSize: 13, color: '#92400E' }}>그룹 예약 {preview.temp.bookingCount}건</span>
            )}
            {(preview.temp.memberTicketCount ?? 0) > 0 && (
                <span style={{ fontSize: 13, color: '#92400E' }}>그룹 수강권 {preview.temp.memberTicketCount}건</span>
            )}
            {(preview.temp.subscriptionCount ?? 0) > 0 && (
                <span style={{ fontSize: 13, color: '#92400E' }}>시설 이용권 {preview.temp.subscriptionCount}건</span>
            )}
        </Col>
        {/* arrow */}
        <Col style={{ justifyContent: 'center', alignItems: 'center', flexShrink: 0 }}>
            <span style={{ fontSize: 24, color: '#9CA3AF' }}>→</span>
        </Col>
        {/* 연동 회원 */}
        <Col style={{ flex: 1, padding: 16, borderRadius: 10, border: '1px solid #BFDBFE', backgroundColor: '#EFF6FF' }}>
            <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4, backgroundColor: '#2563EB', color: '#fff', alignSelf: 'flex-start', marginBottom: 10 }}>
                유지되는 연동 회원
            </span>
            <span style={{ fontSize: 16, fontWeight: 700, color: '#1E40AF' }}>{preview.original.name}</span>
            <span style={{ fontSize: 13, color: '#1E40AF', marginTop: 2 }}>{formatKoreanPhoneNumber(preview.original.phoneNumber, preview.original.phoneNumber)}</span>
            <div style={{ height: 1, backgroundColor: '#BFDBFE', margin: '10px 0' }} />
            <span style={{ fontSize: 13, color: '#1E40AF' }}>PT 수업 {preview.original.scheduleCount}건</span>
            <span style={{ fontSize: 13, color: '#1E40AF' }}>PT 수강권 {preview.original.membershipCount}건</span>
            {(preview.original.bookingCount ?? 0) > 0 && (
                <span style={{ fontSize: 13, color: '#1E40AF' }}>그룹 예약 {preview.original.bookingCount}건</span>
            )}
            {(preview.original.memberTicketCount ?? 0) > 0 && (
                <span style={{ fontSize: 13, color: '#1E40AF' }}>그룹 수강권 {preview.original.memberTicketCount}건</span>
            )}
            {(preview.original.subscriptionCount ?? 0) > 0 && (
                <span style={{ fontSize: 13, color: '#1E40AF' }}>시설 이용권 {preview.original.subscriptionCount}건</span>
            )}
        </Col>
    </Row>
);

export default MemberMergeModal;
